import { useMemo } from 'react';
import { useTenant } from './useTenant';

export interface Visibility {
  /** Company-wide totals, KPIs, LTV, revenue-at-risk, summary tiles. */
  canSeeCompanyFinancials: boolean;
  /** Per-job / per-customer profit figures. */
  canSeeProfit: boolean;
  /** Technician commission rates and commission / override amounts. */
  canSeeTechPay: boolean;
  /** Advertising spend, campaign cost, ROI. */
  canSeeMarketingCosts: boolean;
  /** Admin-only settings sections and admin-only routes. */
  canManageSettings: boolean;
  /** Sees only their own slice (own CSR row, own pipeline): no admin role and no company_financials grant, or roles still loading. */
  isRestricted: boolean;
}

/**
 * Admins and super admins see everything. Every other user (csr, affiliate,
 * no role) sees a finance area only when an admin granted them that
 * permission (Settings → Users → Permissions); Settings stay admin-only.
 * While roles are still loading the user is treated as restricted so nothing
 * sensitive flashes before the roles and grants arrive.
 */
export function useVisibility(): Visibility {
  const { isAdmin, isSuperAdmin, isLoadingMe, permissions } = useTenant();
  const full = !isLoadingMe && (isAdmin || isSuperAdmin);
  const granted = (key: (typeof permissions)[number]) => !isLoadingMe && permissions.includes(key);

  const canSeeCompanyFinancials = full || granted('company_financials');
  const canSeeProfit = full || granted('profit');
  const canSeeTechPay = full || granted('tech_pay');
  const canSeeMarketingCosts = full || granted('marketing_costs');

  return useMemo(
    () => ({
      canSeeCompanyFinancials,
      canSeeProfit,
      canSeeTechPay,
      canSeeMarketingCosts,
      canManageSettings: full,
      isRestricted: !canSeeCompanyFinancials,
    }),
    [full, canSeeCompanyFinancials, canSeeProfit, canSeeTechPay, canSeeMarketingCosts],
  );
}
